import React, { useState, useRef, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { TextField } from "@material-ui/core";
import {
  getMfaStatus,
  setupMfa,
  enableMfa,
  disableMfa,
} from "../crud/auth.crud";

const MfaSetupModal = ({ show, onHide }) => {
  const [step, setStep] = useState("loading");
  const [secret, setSecret] = useState("");
  const [qrCodeSvg, setQrCodeSvg] = useState("");
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (show) {
      checkMfaStatus();
    }
  }, [show]);

  const checkMfaStatus = async () => {
    setStep("loading");
    try {
      const res = await getMfaStatus();
      setStep(res.data.mfa_enabled ? "enabled" : "disabled");
    } catch {
      setError("Erro ao verificar status do MFA.");
      setStep("error");
    }
  };

  const handleSetup = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await setupMfa();
      setSecret(res.data.secret);
      setQrCodeSvg(res.data.qr_code);
      setStep("setup");
    } catch (err) {
      setError(err.response?.data?.error || "Erro ao configurar MFA.");
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    setError("");

    if (value && index < 5 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (
      e.key === "Backspace" &&
      !code[index] &&
      index > 0 &&
      inputRefs.current[index - 1]
    ) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    const newCode = [...code];

    for (let i = 0; i < pastedData.length; i++) {
      newCode[i] = pastedData[i];
    }

    setCode(newCode);
  };

  const handleEnable = async () => {
    const fullCode = code.join("");
    if (fullCode.length !== 6) {
      setError("Digite o código de 6 dígitos.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      await enableMfa(fullCode);
      setStep("success");
    } catch (err) {
      setError(err.response?.data?.error || "Código inválido.");
      setCode(["", "", "", "", "", ""]);
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    const fullCode = code.join("");
    if (fullCode.length !== 6) {
      setError("Digite o código de 6 dígitos.");
      return;
    }
    if (!password) {
      setError("Digite sua senha.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      await disableMfa(password, fullCode);
      setStep("disabled");
      setCode(["", "", "", "", "", ""]);
      setPassword("");
    } catch (err) {
      setError(err.response?.data?.error || "Erro ao desabilitar MFA.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCode(["", "", "", "", "", ""]);
    setPassword("");
    setError("");
    setSecret("");
    setQrCodeSvg("");
    setCopied(false);
    onHide();
  };

  const handleCopySecret = () => {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const renderCodeInputs = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        gap: "8px",
        marginBottom: "20px",
      }}
    >
      {code.map((digit, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={(e) => handleCodeChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          style={{
            width: "40px",
            height: "50px",
            fontSize: "20px",
            textAlign: "center",
            border: "1px solid #e2e5ec",
            borderRadius: "4px",
          }}
          disabled={loading}
        />
      ))}
    </div>
  );

  const renderContent = () => {
    switch (step) {
      case "loading":
        return (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Carregando...</span>
            </div>
          </div>
        );

      case "error":
        return (
          <div className="text-center py-4">
            <p className="text-danger">{error}</p>
            <Button variant="primary" onClick={checkMfaStatus}>
              Tentar novamente
            </Button>
          </div>
        );

      case "disabled":
        return (
          <div className="text-center py-4">
            <i
              className="fas fa-shield-alt fa-3x text-muted mb-3"
              style={{ display: "block" }}
            ></i>
            <h5>Autenticação em duas etapas</h5>
            <p className="text-muted">
              Adicione uma camada extra de segurança à sua conta usando um
              aplicativo autenticador.
            </p>
            <Button variant="primary" onClick={handleSetup} disabled={loading}>
              {loading ? "Configurando..." : "Configurar MFA"}
            </Button>
          </div>
        );

      case "setup":
        return (
          <div className="text-center">
            <h5>Escaneie o QR Code</h5>
            <p className="text-muted small">
              Use um aplicativo autenticador como Google Authenticator ou Authy.
            </p>

            <div
              className="mb-3"
              style={{ display: "flex", justifyContent: "center" }}
            >
              <div dangerouslySetInnerHTML={{ __html: atob(qrCodeSvg) }} />
            </div>

            <div className="mb-3">
              <small className="text-muted">Ou insira manualmente:</small>
              <div
                onClick={handleCopySecret}
                style={{
                  background: copied ? "#e8f5e9" : "#f7f8fa",
                  padding: "10px",
                  borderRadius: "4px",
                  fontFamily: "monospace",
                  fontSize: "14px",
                  wordBreak: "break-all",
                  cursor: "pointer",
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  border: copied ? "1px solid #4caf50" : "1px solid #e2e5ec",
                  transition: "all 0.2s ease",
                }}
                title="Clique para copiar"
              >
                <span>{secret}</span>
                {copied ? (
                  <span
                    style={{
                      color: "#4caf50",
                      marginLeft: "10px",
                      fontWeight: "500",
                      fontSize: "12px",
                    }}
                  >
                    <i className="fas fa-check"></i> Copiado!
                  </span>
                ) : (
                  <i
                    className="fas fa-copy"
                    style={{ color: "#5d78ff", marginLeft: "10px" }}
                  ></i>
                )}
              </div>
            </div>

            <hr />

            <p className="text-muted small mb-2">
              Digite o código do aplicativo para confirmar:
            </p>

            {error && (
              <div className="alert alert-danger py-2 mb-3">
                <small>{error}</small>
              </div>
            )}

            {renderCodeInputs()}

            <Button
              variant="primary"
              onClick={handleEnable}
              disabled={loading || code.join("").length !== 6}
              className="w-100"
            >
              {loading ? "Verificando..." : "Ativar MFA"}
            </Button>
          </div>
        );

      case "enabled":
        return (
          <div className="text-center py-3">
            <i
              className="fas fa-check-circle fa-3x text-success mb-3"
              style={{ display: "block" }}
            ></i>
            <h5>MFA está ativo</h5>
            <p className="text-muted">
              Sua conta está protegida com autenticação em duas etapas.
            </p>

            <hr />

            <h6>Desativar MFA</h6>
            <p className="text-muted small">
              Para desativar, digite sua senha e o código atual.
            </p>

            {error && (
              <div className="alert alert-danger py-2 mb-3">
                <small>{error}</small>
              </div>
            )}

            <TextField
              type="password"
              label="Sua senha"
              margin="dense"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              variant="outlined"
              size="small"
              className="mb-3"
            />

            {renderCodeInputs()}

            <Button
              variant="danger"
              onClick={handleDisable}
              disabled={loading || code.join("").length !== 6 || !password}
              className="w-100"
            >
              {loading ? "Desativando..." : "Desativar MFA"}
            </Button>
          </div>
        );

      case "success":
        return (
          <div className="text-center py-4">
            <i
              className="fas fa-check-circle fa-3x text-success mb-3"
              style={{ display: "block" }}
            ></i>
            <h5>MFA ativado com sucesso!</h5>
            <p className="text-muted">
              Sua conta agora está protegida com autenticação em duas etapas.
            </p>
            <Button variant="primary" onClick={handleClose}>
              Fechar
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Segurança da Conta</Modal.Title>
      </Modal.Header>
      <Modal.Body>{renderContent()}</Modal.Body>
    </Modal>
  );
};

export default MfaSetupModal;
